import { useState, useEffect } from 'react';
import { useUnifiedAuth } from '../../hooks/useUnifiedAuth';
import { UnifiedAuthResult, AuthMethod } from '../../libs/UnifiedAuthService';
import { EZIOKProvider } from '../../libs/EZIOKService';
import UnifiedAuthModal from '../../components/UnifiedAuthModal/UnifiedAuthModal';
import styles from './MobileOKTest.module.scss';

/**
 * 통합 본인확인 테스트 페이지
 *
 * 휴대폰 인증 (Mobile-OK)과 간편인증 (EZ-IOK) 통합 테스트
 *
 * 프론트엔드: http://localhost:8081/mobileok-test
 * 백엔드 프록시: http://localhost:8081 -> package.json proxy 설정
 */
function MobileOKTest() {
  const [result, setResult] = useState<UnifiedAuthResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedAuthMethod, setSelectedAuthMethod] = useState<AuthMethod>('mobile');
  const [selectedProvider, setSelectedProvider] = useState<string>('');
  const [eziokProviders, setEziokProviders] = useState<EZIOKProvider[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const {
    startAuth,
    getEZIOKProviders,
    isLoading,
    sdkStatus,
  } = useUnifiedAuth({
    onSuccess: (data) => {
      console.log('[AuthTest] 인증 성공:', data);
      setResult(data);
      setError(null);
    },
    onError: (err) => {
      console.error('[AuthTest] 인증 실패:', err);
      setError(err.message);
      setResult(null);
    },
  });

  // 초기 데이터 로드
  useEffect(() => {
    const loadData = async () => {
      const providers = await getEZIOKProviders();
      setEziokProviders(providers);
    };
    loadData();
  }, [getEZIOKProviders]);

  // 팝업에서 postMessage로 전달받는 결과 리스너
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;

      // Mobile-OK 결과
      if (event.data.type === 'MOBILEOK_SUCCESS') {
        setResult({
          authMethod: 'mobile',
          ...event.data.data,
        });
        setError(null);
      } else if (event.data.type === 'MOBILEOK_ERROR') {
        setError(event.data.error);
        setResult(null);
      }
      // EZ-IOK 결과
      else if (event.data.type === 'EZIOK_SUCCESS') {
        setResult({
          authMethod: 'easy',
          ...event.data.data,
        });
        setError(null);
      } else if (event.data.type === 'EZIOK_ERROR') {
        setError(event.data.error);
        setResult(null);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  // API Base URL
  const API_BASE = process.env.REACT_APP_API_BASE_URL || 'https://api.ondoc.co.kr';

  // 휴대폰 인증 시작
  const handleMobileOKAuth = async () => {
    setError(null);
    setResult(null);
    try {
      await startAuth({
        authMethod: 'mobile',
        resultUrl: `${API_BASE}/func/mobileok/callback`,
        usageCode: '01005',
        serviceType: 'telcoAuth',
      });
    } catch (err) {
      // 에러는 훅에서 처리됨
    }
  };

  // 간편인증 시작
  const handleEZIOKAuth = async () => {
    setError(null);
    setResult(null);
    try {
      await startAuth({
        authMethod: 'easy',
        resultUrl: `${API_BASE}/func/eziok/callback`,
        authProviderCode: selectedProvider || undefined,
        eziokServiceType: 'auth',
      });
    } catch (err) {
      // 에러는 훅에서 처리됨
    }
  };

  // 결과 테이블 렌더링
  const renderResultTable = () => {
    if (!result) return null;

    return (
      <table className={styles.resultTable}>
        <tbody>
          <tr>
            <th>인증방식</th>
            <td>{result.authMethod === 'mobile' ? '휴대폰 인증' : '간편인증'}</td>
          </tr>
          <tr>
            <th>결과코드</th>
            <td>{result.resultCode}</td>
          </tr>
          <tr>
            <th>결과메시지</th>
            <td>{result.resultMsg}</td>
          </tr>
          <tr>
            <th>이름</th>
            <td>{result.name || '-'}</td>
          </tr>
          <tr>
            <th>생년월일</th>
            <td>{result.birthDate || '-'}</td>
          </tr>
          <tr>
            <th>성별</th>
            <td>{result.gender === 'M' ? '남성' : result.gender === 'F' ? '여성' : '-'}</td>
          </tr>
          {result.mobileNo && (
            <tr>
              <th>휴대폰번호</th>
              <td>{result.mobileNo}</td>
            </tr>
          )}
          {result.mobileCo && (
            <tr>
              <th>통신사</th>
              <td>{result.mobileCo}</td>
            </tr>
          )}
          {result.authProvider && (
            <tr>
              <th>인증사업자</th>
              <td>{result.authProvider}</td>
            </tr>
          )}
          <tr>
            <th>CI</th>
            <td className={styles.mono}>
              {result.ci ? `${result.ci.substring(0, 20)}...` : '-'}
            </td>
          </tr>
          <tr>
            <th>DI</th>
            <td className={styles.mono}>
              {result.di ? `${result.di.substring(0, 20)}...` : '-'}
            </td>
          </tr>
        </tbody>
      </table>
    );
  };

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>통합 본인확인 테스트</h1>

      {/* 통합 인증 모달 테스트 */}
      <div className={styles.card}>
        <h2>통합 인증 모달 (신규)</h2>
        <p className={styles.description}>
          아이콘 기반 인증 방식 선택 팝업
        </p>
        <button
          type="button"
          className={styles.verifyButton}
          onClick={() => setIsModalOpen(true)}
        >
          통합 인증 모달 열기
        </button>
      </div>

      {/* 통합 인증 모달 */}
      <UnifiedAuthModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={(eziokResult) => {
          console.log('[AuthModal] 인증 성공:', eziokResult);
          // EZIOKResult를 UnifiedAuthResult로 변환
          setResult({
            resultCode: eziokResult.success ? '0' : '1',
            resultMsg: eziokResult.message,
            authMethod: 'easy',
            name: eziokResult.data.name,
            ci: eziokResult.data.ci,
            di: eziokResult.data.di,
            birthDate: eziokResult.data.birthDate,
            gender: eziokResult.data.gender,
            nationalInfo: eziokResult.data.nationalInfo,
            mobileNo: eziokResult.data.mobileNo,
          });
          setError(null);
        }}
        onError={(err) => {
          console.error('[AuthModal] 인증 실패:', err);
          setError(err.message);
          setResult(null);
        }}
      />

      {/* SDK 상태 */}
      <div className={styles.card}>
        <h2>SDK 로드 상태</h2>
        <div className={styles.sdkStatus}>
          <span className={sdkStatus.mobileOK ? styles.statusOk : styles.statusError}>
            Mobile-OK: {sdkStatus.mobileOK ? '로드됨' : '로드 중...'}
          </span>
          <span className={sdkStatus.eziok ? styles.statusOk : styles.statusError}>
            EZ-IOK: {sdkStatus.eziok ? '로드됨' : '로드 중...'}
          </span>
        </div>
      </div>

      {/* 인증 방식 선택 */}
      <div className={styles.card}>
        <h2>인증 방식 선택</h2>
        <div className={styles.authMethodTabs}>
          <button
            type="button"
            className={`${styles.tabButton} ${selectedAuthMethod === 'mobile' ? styles.active : ''}`}
            onClick={() => setSelectedAuthMethod('mobile')}
          >
            휴대폰 인증 (Mobile-OK)
          </button>
          <button
            type="button"
            className={`${styles.tabButton} ${selectedAuthMethod === 'easy' ? styles.active : ''}`}
            onClick={() => setSelectedAuthMethod('easy')}
          >
            간편인증 (EZ-IOK)
          </button>
        </div>
      </div>

      {/* 휴대폰 인증 섹션 */}
      {selectedAuthMethod === 'mobile' && (
        <div className={styles.card}>
          <h2>휴대폰 본인확인</h2>
          <p className={styles.description}>
            SMS 인증번호를 통한 휴대폰 본인확인
          </p>
          <button
            type="button"
            className={`${styles.verifyButton} ${isLoading ? styles.loading : ''}`}
            onClick={handleMobileOKAuth}
            disabled={isLoading || !sdkStatus.mobileOK}
          >
            {isLoading ? '처리 중...' : '휴대폰 본인확인'}
          </button>
        </div>
      )}

      {/* 간편인증 섹션 */}
      {selectedAuthMethod === 'easy' && (
        <div className={styles.card}>
          <h2>간편인증</h2>
          <p className={styles.description}>
            카카오, PASS, 네이버 등 간편인증 앱을 통한 본인확인
          </p>

          {/* 인증사업자 선택 */}
          <div className={styles.providerSection}>
            <label className={styles.label}>인증사업자 선택 (통합창 사용 시 필수)</label>
            <select
              className={styles.select}
              value={selectedProvider}
              onChange={(e) => setSelectedProvider(e.target.value)}
            >
              <option value="">선택하세요</option>
              {eziokProviders.map((provider) => (
                <option key={provider.code} value={provider.code}>
                  {provider.name} ({provider.type})
                </option>
              ))}
            </select>
            <p className={styles.hint}>
              * 통합창(eziok_std_process1) 사용 시 인증사업자 선택 필수
            </p>
          </div>

          <button
            type="button"
            className={`${styles.verifyButton} ${isLoading ? styles.loading : ''}`}
            onClick={handleEZIOKAuth}
            disabled={isLoading || !sdkStatus.eziok || !selectedProvider}
          >
            {isLoading ? '처리 중...' : '간편인증'}
          </button>
        </div>
      )}

      {/* 에러 표시 */}
      {error && (
        <div className={styles.errorCard}>
          <h2>에러 발생</h2>
          <p>{error}</p>
        </div>
      )}

      {/* 인증 결과 */}
      {result && (
        <div className={styles.resultCard}>
          <h2>인증 결과</h2>
          {renderResultTable()}
        </div>
      )}

      {/* API 엔드포인트 정보 */}
      <div className={styles.card}>
        <h2>API 엔드포인트</h2>
        <div className={styles.apiSection}>
          <h3>휴대폰 인증 (Mobile-OK)</h3>
          <ul className={styles.apiList}>
            <li>
              <strong>GET/POST</strong> /func/mobileok/request
              <span>인증 요청</span>
            </li>
            <li>
              <strong>POST</strong> /func/mobileok/result
              <span>결과 조회</span>
            </li>
            <li>
              <strong>POST</strong> /func/mobileok/callback
              <span>콜백 처리</span>
            </li>
          </ul>
        </div>

        <div className={styles.apiSection}>
          <h3>간편인증 (EZ-IOK)</h3>
          <ul className={styles.apiList}>
            <li>
              <strong>GET</strong> https://api.ondoc.co.kr/func/eziok/providers
              <span>인증사업자 목록</span>
            </li>
            <li>
              <strong>GET/POST</strong> /func/eziok/request
              <span>인증 요청</span>
            </li>
            <li>
              <strong>POST</strong> /func/eziok/result
              <span>결과 조회</span>
            </li>
            <li>
              <strong>POST</strong> /func/eziok/callback
              <span>콜백 처리</span>
            </li>
          </ul>
        </div>

        <div className={styles.apiSection}>
          <h3>통합 인증</h3>
          <ul className={styles.apiList}>
            <li>
              <strong>GET</strong> /func/identity/methods
              <span>인증 방식 목록</span>
            </li>
            <li>
              <strong>POST</strong> /func/identity/request
              <span>통합 인증 요청</span>
            </li>
            <li>
              <strong>POST</strong> /func/identity/result
              <span>통합 인증 결과</span>
            </li>
          </ul>
        </div>
      </div>

      {/* 콜백 URL 정보 */}
      <div className={styles.card}>
        <h2>콜백 리다이렉트 URL</h2>
        <ul className={styles.apiList}>
          <li>
            <strong>휴대폰</strong>
            <span className={styles.mono}>https://api.ondoc.co.kr/func/mobileok/callback</span>
          </li>
          <li>
            <strong>간편인증</strong>
            <span className={styles.mono}>https://api.ondoc.co.kr/func/eziok/callback</span>
          </li>
        </ul>
      </div>
    </div>
  );
}

export default MobileOKTest;